import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { useAgent } from '../context/AgentContext';
import { projects as projectsApi } from '../services/api';
import TaskCard from '../components/TaskCard';
import TaskDetailModal from '../components/TaskDetailModal';
import NewTicketModal from '../components/NewTicketModal';
import AlertModal from '../components/AlertModal';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'complete', label: 'Complete' },
];

const SORTS = [
  { id: 'recent', label: 'Recent' },
  { id: 'priority', label: 'Priority' },
];

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const TaskBoard = () => {
  const { selectedProject } = useProject();
  const { tasks, refreshTasks, loading } = useAgent();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [deployLoading, setDeployLoading] = useState(false);
  const [deployMessage, setDeployMessage] = useState(null);
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);

  const filterTasks = (tasks) => {
    let filtered = [...tasks];

    if (filter === 'active') {
      filtered = filtered.filter(t => t.status !== 'complete');
    } else if (filter === 'complete') {
      filtered = filtered.filter(t => t.status === 'complete');
    }

    if (sort === 'recent') {
      filtered.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    } else if (sort === 'priority') {
      filtered.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1));
    }

    return filtered;
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = async () => {
    await refreshTasks();
    setSelectedTask(null);
  };

  // Keep selectedTask in sync with SSE-updated tasks array
  const currentTask = selectedTask
    ? tasks.find(t => t.id === selectedTask.id) || selectedTask
    : null;

  const handleTaskCreated = async () => {
    setShowNewTaskModal(false);
    await refreshTasks();
  };

  const handleDeployConfirm = async () => {
    if (!selectedProject) return;
    setShowDeployConfirm(false);
    setDeployLoading(true);
    setDeployMessage(null);

    try {
      await projectsApi.deploy(selectedProject.id);
      setDeployMessage({ type: 'success', text: 'Deploy job submitted successfully!' });
      setTimeout(() => setDeployMessage(null), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to deploy project';
      setDeployMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setDeployMessage(null), 5000);
    } finally {
      setDeployLoading(false);
    }
  };

  const filteredTasks = filterTasks(tasks);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Task Board</h1>
          {selectedProject && (
            <p>{selectedProject.name} - {selectedProject.description}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {selectedProject && selectedProject.deploy_script && (
            <button
              className="btn-primary"
              onClick={() => setShowDeployConfirm(true)}
              disabled={deployLoading}
            >
              {deployLoading ? 'Deploying...' : 'Deploy'}
            </button>
          )}
          {selectedProject && (
            <button
              className="btn-primary"
              onClick={() => setShowNewTaskModal(true)}
            >
              + New Task
            </button>
          )}
        </div>
      </div>

      {deployMessage && (
        <div className={`alert ${deployMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '20px' }}>
          {deployMessage.text}
        </div>
      )}

      {selectedProject ? (
        <>
          {/* Filter Bar */}
          <div className="task-board__filters">
            <div className="task-board__filter-group">
              {FILTERS.map(f => (
                <button
                  key={f.id}
                  className={`task-board__filter-btn ${filter === f.id ? 'task-board__filter-btn--active' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="task-board__filter-group">
              {SORTS.map(s => (
                <button
                  key={s.id}
                  className={`task-board__filter-btn ${sort === s.id ? 'task-board__filter-btn--active' : ''}`}
                  onClick={() => setSort(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <span className="task-board__count">
              {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Task Grid */}
          {loading && tasks.length === 0 ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : filteredTasks.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-tertiary)' }}>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No tasks found</p>
              <p style={{ fontSize: '13px' }}>Create a new task to get started</p>
            </div>
          ) : (
            <div className="task-grid">
              {filteredTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={() => handleTaskClick(task)}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
            No project selected. Please select a project from the sidebar.
          </p>
        </div>
      )}

      {currentTask && (
        <TaskDetailModal
          task={currentTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
        />
      )}

      {showNewTaskModal && (
        <NewTicketModal
          projectId={selectedProject.id}
          initialStatus="todo"
          onClose={() => setShowNewTaskModal(false)}
          onCreate={handleTaskCreated}
        />
      )}

      <AlertModal
        isOpen={showDeployConfirm}
        onClose={() => setShowDeployConfirm(false)}
        title="Deploy Project"
        message={`Are you sure you want to deploy "${selectedProject?.name}"?`}
        type="confirm"
        confirmText="Deploy"
        onConfirm={handleDeployConfirm}
      />
    </div>
  );
};

export default TaskBoard;
